import { Restricted } from 'components/auth';
import { GoogleAnalytics } from 'components/tracking/GoogleAnalytics';
import { HotJarTracker } from 'components/tracking/HotJarTracker';
import { WhiteLabelContextProvider } from 'components/white-label';
import { EnvVariable, getEnv } from 'lib/helpers/envHelper';
import {
  buildQueryParamLink,
  BuildQueryParamLinkQueryParams,
} from 'lib/helpers/urlHelper';
import { Organization } from 'lib/types/company';
import _ from 'lodash';
import { ReactNode, useState } from 'react';
import { updateUserSetting as apiUpdateUserSetting } from '../api';
import { DEFAULT_METADATA } from '../constants';
import { LearningCenterContext } from '../context';
import { ThemeProvider } from '../theme';
import {
  ILearningCenterMetadata,
  ILearningCenterUserSettingsView,
  LearningCenterMode,
  LearningCenterUserSettingKey,
  LearningCenterUserSettingValue,
} from '../types';
import { useIdentifySentryUser, useLearningCenterUrlParams } from './hooks';

interface ILearningCenterContextProviderProps {
  children: ReactNode;
  host?: string;
  ignoreTheme?: boolean;
  // This property is required in order to ensure that engineers are forced to
  // pass a value in, even if `undefined`.
  metadata: Partial<ILearningCenterMetadata>;
  previewKey?: string;
  // This property is required in order to ensure that engineers are forced to
  // pass a value in, even if `undefined`.
  mode: LearningCenterMode;
  scormKey?: string;
}

export const LearningCenterContextProvider = ({
  children,
  host,
  ignoreTheme,
  metadata: passedMetadata,
  previewKey,
  mode,
  scormKey: passedScormKey,
}: ILearningCenterContextProviderProps) => {
  const metadata: ILearningCenterMetadata = _.defaultsDeep(
    passedMetadata,
    DEFAULT_METADATA
  );

  const [userSettings, setUserSettings] =
    useState<ILearningCenterUserSettingsView>(metadata.userSettings);

  const isClassroomMode = mode === LearningCenterMode.CLASSROOM;
  const isClassroomPreviewMode = mode === LearningCenterMode.CLASSROOM_PREVIEW;
  const isCmsMode = mode === LearningCenterMode.CMS;
  const isLearningLibraryMode = mode === LearningCenterMode.LEARNING_LIBRARY;
  // TODO: We use `isPreviewMode` for both training previews and CMS previews, but moving forward we should use `isCmsMode` for CMS previews.
  // This means we'll need to update all the places that check `isPreviewMode` to check `isCmsMode` instead.
  const isPreviewMode =
    mode === LearningCenterMode.PREVIEW || mode === LearningCenterMode.CMS;
  const isTrainingMode = mode === LearningCenterMode.TRAINING;

  const urlParams = useLearningCenterUrlParams({
    ssoConnection: metadata.ssoConnection,
  });

  useIdentifySentryUser({
    companyId: metadata?.companyId,
    userId: metadata?.userId,
  });

  // Important: The URL is manipulated by the Learner Dashboard, so we need to
  // wait until the `sso` param is set to avoid a race condition.
  // See: https://ethena-team.monday.com/boards/3831714395/pulses/8746904945
  if (metadata.ssoConnection && !urlParams.sso && !urlParams.kioskKey) {
    return null;
  }

  const scormKey = passedScormKey || urlParams.scormKey;

  const buildLearningCenterUrl = (
    path: string,
    queryParams?: BuildQueryParamLinkQueryParams
  ) =>
    buildQueryParamLink(path, {
      ...queryParams,
      classroomKey: urlParams.classroomKey,
      company: urlParams.company,
      key: isPreviewMode ? previewKey : undefined,
      kioskKey: urlParams.kioskKey,
      scormKey,
      sso: urlParams.kioskKey ? undefined : urlParams.sso, // kiosk bypasses SSO
    });

  const updateUserSetting = async (
    key: LearningCenterUserSettingKey,
    value: LearningCenterUserSettingValue
  ) => {
    setUserSettings((prev) => ({ ...prev, [key]: value }));

    if (!isClassroomMode && !isClassroomPreviewMode && !isPreviewMode) {
      await apiUpdateUserSetting({ settingKey: key, value });
    }
  };

  const getRedirectPath = (): string => {
    if (urlParams.kioskKey) {
      return '/learning/kiosk';
    }
    if (urlParams.sso) {
      return '/learning';
    }
    return '/learning/login';
  };

  const logoutUrl = buildQueryParamLink(
    `${getEnv(EnvVariable.API_PATHNAME)}/logout`,
    {
      redirectPath: getRedirectPath(),
    }
  );

  return (
    <WhiteLabelContextProvider host={host} organization={metadata.organization}>
      <ThemeProvider properties={ignoreTheme ? undefined : metadata.theme}>
        <LearningCenterContext.Provider
          value={{
            ...metadata,
            buildLearningCenterUrl,
            isAuthenticated: !!passedMetadata,
            isClassroomMode,
            isClassroomPreviewMode,
            isCmsMode,
            isLearningLibraryMode,
            isPreviewMode,
            isTrainingMode,
            kioskKey: urlParams.kioskKey,
            logoutUrl,
            mode,
            scormKey,
            ssoConnection: urlParams.sso,
            userSettings,
            updateUserSetting,
          }}
        >
          <Restricted toOrganizations={[Organization.LIFE_LABS]}>
            <GoogleAnalytics
              companyId={metadata.companyId}
              userId={metadata.userId}
            />
          </Restricted>
          <HotJarTracker />
          {children}
        </LearningCenterContext.Provider>
      </ThemeProvider>
    </WhiteLabelContextProvider>
  );
};
